import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as adminApi from '../api/adminApi';
import { TICKET_STATUSES, formatDate } from '../lib/format';
import { pickErrorMessage } from '../lib/apiHelpers';
import { PageHeader, Card, CardBody, ErrorBanner, BackLink } from '../components/PageChrome';
import { Modal } from '../components/Modal';

function ticketStatusBadge(status) {
  const s = String(status || '').toUpperCase();
  const map = {
    OPEN: 'warning',
    IN_PROGRESS: 'info',
    CLOSED: 'secondary',
  };
  return <span className={`badge text-bg-${map[s] || 'secondary'}`}>{s || '—'}</span>;
}

export default function SupportTicketDetail() {
  const { ticketId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusModal, setStatusModal] = useState(false);
  const [nextStatus, setNextStatus] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await adminApi.getSupportTicket(ticketId);
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setError(pickErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ticketId]);

  async function saveStatus(e) {
    e.preventDefault();
    if (!nextStatus) return;
    setSaving(true);
    try {
      await adminApi.patchSupportTicketStatus(ticketId, { status: nextStatus });
      const res = await adminApi.getSupportTicket(ticketId);
      setData(res);
      setStatusModal(false);
    } catch (err) {
      setError(pickErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  const userId = data?.userId;

  return (
    <>
      <BackLink to="/support-tickets">Support tickets</BackLink>
      <PageHeader
        title={data?.subject ? String(data.subject) : `Ticket ${ticketId}`}
        subtitle={ticketId}
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setStatusModal(true)}>
            Update status
          </button>
        }
      />
      <ErrorBanner message={error} />
      <Card>
        <CardBody className="p-3 p-md-4">
          <dl className="row mb-0">
            <dt className="col-sm-3 text-secondary">Status</dt>
            <dd className="col-sm-9">{ticketStatusBadge(data?.status)}</dd>
            <dt className="col-sm-3 text-secondary">Subject</dt>
            <dd className="col-sm-9 fw-semibold">{data?.subject || '—'}</dd>
            <dt className="col-sm-3 text-secondary">Message</dt>
            <dd className="col-sm-9 text-break">{data?.message || '—'}</dd>
            <dt className="col-sm-3 text-secondary">Customer</dt>
            <dd className="col-sm-9">
              {userId ? (
                <Link to={`/app-users/${userId}`} className="font-monospace small text-decoration-none">
                  {userId}
                </Link>
              ) : (
                '—'
              )}
            </dd>
            <dt className="col-sm-3 text-secondary">Opened</dt>
            <dd className="col-sm-9">{formatDate(data?.createdAt || data?.created_at)}</dd>
            <dt className="col-sm-3 text-secondary">Updated</dt>
            <dd className="col-sm-9">{formatDate(data?.updatedAt || data?.updated_at)}</dd>
          </dl>
        </CardBody>
      </Card>

      {statusModal ? (
        <Modal
          title="Update ticket status"
          onClose={() => setStatusModal(false)}
          footer={
            <>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setStatusModal(false)} disabled={saving}>
                Cancel
              </button>
              <button type="submit" form="ticket-status-form" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <form id="ticket-status-form" onSubmit={saveStatus}>
            <label className="form-label">Status</label>
            <select className="form-select" value={nextStatus} onChange={(e) => setNextStatus(e.target.value)} required>
              <option value="" disabled>
                Select…
              </option>
              {TICKET_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </form>
        </Modal>
      ) : null}
    </>
  );
}
